import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, CheckCircle, Clock, AlertTriangle, 
  UserPlus, MapPin, Calendar, Bell, Download, Plus
} from 'lucide-react';
import { getThemeStyles, Theme } from '../../util/style';

const StatCard = ({ label, value, sub, icon, color, theme }: { label: string; value: string; sub: string; icon: React.ReactNode; color: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
  <div className={`relative ${s.card} border ${s.cardBorder} rounded-xl p-4 sm:p-5 overflow-hidden group ${s.hover} transition-all`}>
    <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${color}`} />
    <div className={`text-[10px] ${s.textSecondary} uppercase tracking-wider mb-2`}>{label}</div>
    <div className={`font-serif text-2xl sm:text-3xl mb-1 ${s.text}`}>{value}</div>
    <div className={`text-xs ${s.textSecondary}`}>{sub}</div>
    <div className={`absolute right-3 top-3 sm:right-4 sm:top-4 ${s.textSecondary} group-hover:${s.accent} transition-colors`}>{icon}</div>
  </div>
  );
};

const QuickAction = ({ icon, label, theme }: { icon: React.JSX.Element; label: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
  <button className={`${s.card} border ${s.cardBorder} rounded-xl p-4 flex flex-col items-center gap-2 ${s.hover} bg-[#00d4aa]/5 transition-all`}>
    {icon}
    <span className={`text-xs font-medium ${s.textSecondary}`}>{label}</span>
  </button>
  );
};

const CardHeader = ({ title, action, className, theme }: { title: string; action: string; className: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
  <div className={`flex items-center justify-between p-4 sm:p-5 border-b ${s.border} ${className}`}>
    <h3 className={`text-sm font-semibold ${s.text}`}>{title}</h3>
    <span className={`text-xs ${s.accent} cursor-pointer hover:underline`}>{action}</span>
  </div>
  );
};

const AttendanceRow = ({ name, id, in: timeIn, out, time, status, statusColor, theme }: { name: string; id: string; in: string; out: string; time: string; status: string; statusColor: 'green' | 'yellow' | 'red'; theme: Theme }) => {
  const s = getThemeStyles(theme);
  const colors = {
    green: 'bg-[#00d4aa]/10 text-[#00d4aa]',
    yellow: 'bg-[#ffd166]/10 text-[#ffd166]',
    red: 'bg-[#ff6b6b]/10 text-[#ff6b6b]',
  };
  return (
    <tr className={`${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}>
        <td className="px-5 py-3">
            <div className={`font-medium ${s.text}`}>{name}</div>
            <div className={`text-[11px] ${s.textSecondary}`}>NIK: {id}</div>
        </td>
        <td className={`px-5 py-3 ${s.text}`}>{timeIn}</td>
        <td className={`px-5 py-3 ${s.text}`}>{out}</td>
        <td className={`px-5 py-3 ${s.text}`}>{time}</td>
        <td className="px-5 py-3">
            <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${colors[statusColor]}`}>{status}</span>
        </td>
    </tr>
  );
};

const AttendanceCardRow = ({ name, id, in: timeIn, out, time, status, statusColor, theme }: { name: string; id: string; in: string; out: string; time: string; status: string; statusColor: 'green' | 'yellow' | 'red'; theme: Theme }) => {
  const s = getThemeStyles(theme);
  const colors = {
    green: 'bg-[#00d4aa]/10 text-[#00d4aa]',
    yellow: 'bg-[#ffd166]/10 text-[#ffd166]',
    red: 'bg-[#ff6b6b]/10 text-[#ff6b6b]',
  };
  return (
    <div className={`p-4 border-b ${s.border} last:border-b-0`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className={`font-medium text-sm ${s.text}`}>{name}</div>
          <div className={`text-[11px] ${s.textSecondary}`}>NIK: {id}</div>
        </div>
        <span className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-bold ${colors[statusColor]}`}>{status}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className={`text-[9px] uppercase tracking-wider ${s.textSecondary} mb-0.5`}>Clock In</div>
          <div className={`text-xs font-medium ${s.text}`}>{timeIn}</div>
        </div>
        <div>
          <div className={`text-[9px] uppercase tracking-wider ${s.textSecondary} mb-0.5`}>Clock Out</div>
          <div className={`text-xs font-medium ${s.text}`}>{out}</div>
        </div>
        <div>
          <div className={`text-[9px] uppercase tracking-wider ${s.textSecondary} mb-0.5`}>Work Time</div>
          <div className={`text-xs font-medium ${s.text}`}>{time}</div>
        </div>
      </div>
    </div>
  );
};

const ActivityItem = ({ color, text, time, theme }: { color: string; text: React.ReactNode; time: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
    <div className="flex gap-3">
        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${color}`} />
        <div>
            <div className={`text-xs leading-relaxed ${s.text}`}>{text}</div>
            <div className={`text-[10px] ${theme === 'dark' ? 'text-[#484f58]' : 'text-gray-400'} mt-0.5`}>{time}</div>
        </div>
    </div>
  );
};

const BarRow = ({ label, val, total, color, theme }: { label: string; val: number; total: number; color: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
    <div className="flex items-center gap-3">
        <span className={`text-xs ${s.textSecondary} w-20 shrink-0`}>{label}</span>
            <div className={`flex-1 h-2 ${s.barBg} rounded-full overflow-hidden`}>
                <div className={`h-full ${color}`} style={{ width: `${(val/total)*100}%` }} />
            </div>
        <span className={`text-xs font-bold w-6 text-right ${s.text}`}>{val}</span>
    </div>
  );
};

const LegendItem = ({ color, label, val, theme }: { color: string; label: string; val: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
    <div className="flex items-center gap-1.5">
        <div className={`w-1.5 h-1.5 rounded-full ${color}`} />
            <span className={s.textSecondary}>{label}</span>
        <span className={`ml-auto font-bold ${s.text}`}>{val}</span>
    </div>
  );
};

// ===== Tipe data yang idealnya dikirim backend untuk heatmap kehadiran =====
// events: daftar kejadian di hari itu (clock-in, izin, lembur, libur, dst).
//         Jumlah event tampil sebagai badge di kotak; isi lengkapnya baru
//         muncul saat kotaknya diklik (lewat modal).
// level:  0-4 = intensitas kehadiran hari itu (dipetakan ke warna kotak)
// isOff:  true kalau hari itu libur -> kotak digambar putus-putus (bukan warna solid)
// Backend TIDAK perlu menebak-nebak weekend/hari kerja: kirim langsung
// level/isOff/events apa adanya untuk tanggal yang relevan; grid kalender
// (posisi kolom hari) tetap dihitung dari tanggal aslinya di frontend.
export interface AttendanceEvent {
  title: string;        // contoh: "Budi Santoso clock-in"
  time?: string;         // contoh: "07:58"
  type?: 'hadir' | 'izin' | 'sakit' | 'lembur' | 'terlambat' | 'libur' | string;
}

export interface AttendanceDayData {
  date: string;          // format ISO "YYYY-MM-DD"
  level?: number | null;  // 0-4
  isOff?: boolean;
  events?: AttendanceEvent[];
}

type CalendarCell = {
  day: number;
  date: string;
  level: number;
  isOff: boolean;
  events: AttendanceEvent[];
};

// Bangun daftar kotak untuk satu bulan, linear sesuai urutan tanggal asli
// (tanpa menyusun ke kolom hari tertentu). Jumlah kotak = jumlah hari asli
// di bulan itu (28/29/30/31), dihitung otomatis dari year + month.
function buildMonthGrid(year: number, month: number, data: AttendanceDayData[]): CalendarCell[] {
  const byDate = new Map(data.map(d => [d.date, d]));
  const daysInMonth = new Date(year, month, 0).getDate(); // month: 1-12 (Mei = 5)

  const cells: CalendarCell[] = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const entry = byDate.get(dateStr);
    cells.push({
      day,
      date: dateStr,
      level: entry?.level ?? 0,
      isOff: entry?.isOff ?? false,
      events: entry?.events ?? [],
    });
  }
  return cells;
}

function formatDateLabel(dateStr: string) {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

function eventDotColor(type?: string) {
  switch (type) {
    case 'hadir': return 'bg-[#00d4aa]';
    case 'izin': return 'bg-[#0095ff]';
    case 'sakit': return 'bg-[#ffd166]';
    case 'lembur': return 'bg-[#a855f7]';
    case 'terlambat': return 'bg-[#ff6b6b]';
    case 'libur': return 'bg-slate-400';
    default: return 'bg-slate-400';
  }
}

// ===== Hari libur nasional (termasuk cuti bersama) =====
// Sumbernya JSON publik yang datanya sendiri berasal dari Google Calendar
// dan otomatis diperbarui tiap tahun: github.com/guangrei/APIHariLibur_V2
// Bentuk datanya: { "YYYY-MM-DD": { holiday: boolean, summary: string[] }, ... }
// holiday:true sudah mencakup hari libur nasional MAUPUN cuti bersama —
// keduanya sama-sama hari kosong bagi PNS, jadi kita pakai apa adanya.
const HOLIDAY_SOURCE_URL = 'https://raw.githubusercontent.com/guangrei/APIHariLibur_V2/main/calendar.min.json';

interface NationalHoliday {
  date: string;
  name: string;
}

async function fetchNationalHolidays(year: number): Promise<NationalHoliday[]> {
  const res = await fetch(HOLIDAY_SOURCE_URL);
  if (!res.ok) throw new Error('Gagal mengambil data hari libur nasional');
  const raw: Record<string, { holiday: boolean; summary: string[] }> = await res.json();

  return Object.entries(raw)
    .filter(([date, info]) => date.startsWith(`${year}-`) && info?.holiday)
    .map(([date, info]) => ({ date, name: info.summary[0] }));
}

// Gabungkan data absensi dari backend dengan hari libur nasional:
// tanggal libur ditandai isOff + ditambahkan sebagai event "libur" di kotaknya,
// tanpa menghapus event lain yang mungkin sudah ada di tanggal yang sama.
function mergeWithHolidays(data: AttendanceDayData[], holidays: NationalHoliday[]): AttendanceDayData[] {
  const map = new Map(data.map(d => [d.date, { ...d }]));
  for (const h of holidays) {
    const existing = map.get(h.date);
    map.set(h.date, {
      date: h.date,
      level: existing?.level,
      isOff: true,
      events: [...(existing?.events ?? []), { title: h.name, type: 'libur' }],
    });
  }
  return Array.from(map.values());
}

const DayEventsModal = ({ cell, onClose, theme }: { cell: CalendarCell; onClose: () => void; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className={`${s.card} border ${s.cardBorder} rounded-xl w-full max-w-sm max-h-[80vh] overflow-y-auto`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-4 border-b ${s.border}`}>
          <h3 className={`text-sm font-semibold ${s.text}`}>{formatDateLabel(cell.date)}</h3>
          <button onClick={onClose} className={`text-xs ${s.textSecondary} hover:underline`}>Tutup</button>
        </div>
        <div className="p-4 space-y-3">
          {cell.events.length === 0 && (
            <p className={`text-xs ${s.textSecondary}`}>Tidak ada event tercatat di tanggal ini.</p>
          )}
          {cell.events.map((ev, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${eventDotColor(ev.type)}`} />
              <div>
                <div className={`text-xs font-medium ${s.text}`}>{ev.title}</div>
                {ev.time && <div className={`text-[10px] ${s.textSecondary}`}>{ev.time}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CalendarHeatmap = ({ year, month, data, theme }: { year: number; month: number; data: AttendanceDayData[]; theme: Theme }) => {
  const s = getThemeStyles(theme);
  const [selectedCell, setSelectedCell] = useState<CalendarCell | null>(null);
  const [holidays, setHolidays] = useState<NationalHoliday[]>([]);
  const [holidayError, setHolidayError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHolidayError(false);
    fetchNationalHolidays(year)
      .then(result => { if (!cancelled) setHolidays(result); })
      .catch(() => { if (!cancelled) setHolidayError(true); });
    return () => { cancelled = true; };
  }, [year]);

  const mergedData = useMemo(() => mergeWithHolidays(data, holidays), [data, holidays]);
  const cells = buildMonthGrid(year, month, mergedData);
  const heatmapColors = theme === 'dark'
    ? ['bg-[#1c2330]', 'bg-[#00d4aa]/20', 'bg-[#00d4aa]/40', 'bg-[#00d4aa]/70', 'bg-[#00d4aa]']
    : ['bg-slate-200', 'bg-[#00d4aa]/20', 'bg-[#00d4aa]/40', 'bg-[#00d4aa]/70', 'bg-[#00d4aa]'];

  return (
    <div>
      {holidayError && (
        <p className={`text-[10px] ${s.textSecondary} mb-2`}>Gagal memuat hari libur nasional, menampilkan data lokal saja.</p>
      )}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          const eventCount = c.events.length;
          const isLight = c.level <= 1;
          const baseColor = c.isOff
            ? `border border-dashed ${theme === 'dark' ? 'border-[#30363d]' : 'border-slate-300'}`
            : heatmapColors[c.level];
          return (
            <button
              key={i}
              onClick={() => setSelectedCell(c)}
              className={`relative aspect-square rounded-sm ${baseColor} flex items-center justify-center hover:ring-1 hover:ring-[#00d4aa] transition-all`}
            >
              <span className={`text-[9px] font-medium ${c.isOff || isLight ? s.textSecondary : 'text-white'}`}>{c.day}</span>
              {eventCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-[3px] rounded-full bg-[#ff6b6b] text-white text-[8px] font-bold flex items-center justify-center leading-none">
                  {eventCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selectedCell && (
        <DayEventsModal cell={selectedCell} onClose={() => setSelectedCell(null)} theme={theme} />
      )}
    </div>
  );
};

function DashboardMain({ theme = 'light' }: { theme?: Theme }) {
    const [activeNav, setActiveNav] = useState('Dashboard');
    const s = getThemeStyles(theme);

    // Contoh data dari backend (biasanya hasil GET /attendance/heatmap?year=2026&month=5).
    // Backend cukup kirim apa adanya per tanggal: level (buat warna kotak) dan
    // events (daftar kejadian, ditampilkan sebagai badge angka + isi modal).
    // Hari libur nasional TIDAK perlu dikirim manual dari sini — CalendarHeatmap
    // sudah otomatis fetch & gabungkan sendiri lewat mergeWithHolidays().
    const attendanceData: AttendanceDayData[] = [];

    // Data baris tabel/kartu kehadiran — satu sumber data dipakai untuk
    // render tabel (md ke atas) maupun kartu (mobile), jadi tidak duplikat.
    const attendanceRows: Array<{ name: string; id: string; in: string; out: string; time: string; status: string; statusColor: 'green' | 'yellow' | 'red' }> = [];

    return (
        <main className={`flex-1 min-h-screen ${s.bg}`}>
            <div className="p-4 sm:p-6 lg:p-8">
            {/* STATS GRID */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <StatCard label="Total Karyawan" value="248" sub="+12 dari bulan lalu" icon={<Users className="opacity-20" size={28}/>} color="from-[#00d4aa] to-[#0095ff]" theme={theme} />
                <StatCard label="Hadir Hari Ini" value="211" sub="85% tingkat kehadiran" icon={<CheckCircle className="opacity-20" size={28}/>} color="from-[#0095ff] to-[#a855f7]" theme={theme} />
                <StatCard label="Lembur Bulan Ini" value="47" sub="+8 dari bulan lalu" icon={<Clock className="opacity-20" size={28}/>} color="from-[#ffd166] to-[#f97316]" theme={theme} />
                <StatCard label="Tidak Hadir" value="37" sub="15% dari total" icon={<AlertTriangle className="opacity-20" size={28}/>} color="from-[#ff6b6b] to-[#ec4899]" theme={theme} />
            </div>

            {/* QUICK ACTIONS */}
            <div className="grid grid-cols-2 gap-3 mb-6 sm:mb-8">
                <QuickAction icon={<UserPlus size={24} className="text-[#00d4aa]"/>} label="Tambah Karyawan" theme={theme} />
                {/* <QuickAction icon={<MapPin size={24} className="text-[#0095ff]"/>} label="Catat Kehadiran" theme={theme} /> */}
                {/* <QuickAction icon={<Clock size={24} className="text-[#ffd166]"/>} label="Buat Lembur" theme={theme} /> */}
                <QuickAction icon={<Calendar size={24} className="text-[#ff6b6b]"/>} label="Atur Jadwal" theme={theme} />
            </div>

            {/* MAIN GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* HEATMAP & DONUT */}
                <div className={`${s.card} border ${s.cardBorder} rounded-xl p-4 sm:p-5`}>
                  <CardHeader title="Status Kehadiran" action="Mei 2026" className="!p-0 !mb-4" theme={theme} />
                  <div className="flex flex-wrap gap-4 items-center mb-6">
                      <div className="relative w-20 h-20 shrink-0 rounded-full flex items-center justify-center bg-[conic-gradient(#00d4aa_0%_68%,#0095ff_68%_84%,#ffd166_84%_92%,#ff6b6b_92%_100%)]">
                      <div className={`absolute inset-4 ${s.card} rounded-full flex items-center justify-center text-xs font-bold text-[#00d4aa]`}>68%</div>
                      </div>
                      <div className="flex-1 min-w-[140px] grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                      <LegendItem color="bg-[#00d4aa]" label="Hadir" val="68%" theme={theme} />
                      <LegendItem color="bg-[#0095ff]" label="Izin" val="16%" theme={theme} />
                      <LegendItem color="bg-[#ffd166]" label="Sakit" val="8%" theme={theme} />
                      <LegendItem color="bg-[#ff6b6b]" label="Alpha" val="8%" theme={theme} />
                      </div>
                  </div>
                  <CalendarHeatmap year={2026} month={5} data={attendanceData} theme={theme} />
                </div>

                {/* ACTIVITY FEED */}
                <div className={`${s.card} border ${s.cardBorder} rounded-xl`}>
                <CardHeader title="Aktivitas Terbaru" action="Hari ini" className="" theme={theme} />
                <div className="p-5 space-y-4">
                    <ActivityItem color="bg-[#00d4aa]" text={<span><strong>Budi Santoso</strong> clock-in</span>} time="07:58 — 9 Mei 2025" theme={theme} />
                    <ActivityItem color="bg-[#0095ff]" text={<span>Karyawan baru <strong>Ratna Dewi</strong></span>} time="08:15 — 9 Mei 2025" theme={theme} />
                    <ActivityItem color="bg-[#ffd166]" text={<span><strong>Ahmad Fauzi</strong> ajukan lembur</span>} time="09:30 — 9 Mei 2025" theme={theme} />
                    <ActivityItem color="bg-[#ff6b6b]" text={<span><strong>Dewi Lestari</strong> alpha</span>} time="10:00 — 9 Mei 2025" theme={theme} />
                </div>
                </div>

                {/* BAR CHART: DIVISI */}
                {/* <div className={`${s.card} border ${s.cardBorder} rounded-xl p-5`}>
                <CardHeader title="Karyawan per Divisi" action="Detail →" className="!p-0 !mb-4" theme={theme} />
                <div className="space-y-3">
                    <BarRow label="Teknologi" val={58} total={80} color="bg-[#00d4aa]" theme={theme} />
                    <BarRow label="Keuangan" val={39} total={80} color="bg-[#0095ff]" theme={theme} />
                    <BarRow label="Pemasaran" val={44} total={80} color="bg-[#ffd166]" theme={theme} />
                    <BarRow label="Operasional" val={68} total={80} color="bg-[#ff6b6b]" theme={theme} />
                </div>
                </div> */}

                {/* TABLE KEHADIRAN */}
                <div className={`lg:col-span-2 ${s.card} border ${s.cardBorder} rounded-xl overflow-hidden`}>
                <CardHeader title="Kehadiran Terbaru" action="Lihat semua →" className="" theme={theme} />

                {/* Tampilan tabel untuk layar medium ke atas */}
                <div className="hidden md:block overflow-x-auto">
                <table className={`w-full text-sm ${s.text}`}>
                    <thead>
                    <tr className={`text-[11px] ${s.textSecondary} uppercase tracking-wider border-b ${s.border}`}>
                        <th className="px-5 py-3 text-left font-medium whitespace-nowrap">Karyawan</th>
                        <th className="px-5 py-3 text-left font-medium whitespace-nowrap">Clock In</th>
                        <th className="px-5 py-3 text-left font-medium whitespace-nowrap">Clock Out</th>
                        <th className="px-5 py-3 text-left font-medium whitespace-nowrap">Work Time</th>
                        <th className="px-5 py-3 text-left font-medium whitespace-nowrap">Status</th>
                    </tr>
                    </thead>
                    <tbody className={`divide-y ${theme === 'dark' ? 'divide-[#21262d]/50' : 'divide-gray-200'}`}>
                    {attendanceRows.map(r => (
                      <AttendanceRow key={r.id} {...r} theme={theme} />
                    ))}
                    </tbody>
                </table>
                </div>

                {/* Tampilan kartu untuk mobile */}
                <div className="md:hidden">
                  {attendanceRows.map(r => (
                    <AttendanceCardRow key={r.id} {...r} theme={theme} />
                  ))}
                </div>
                </div>
            </div>
            </div>
        </main>
    );
}


export default DashboardMain;